import {
  MAX_ENERGY,
  ENERGY_PER_MERGE,
  ENERGY_REFILL_INTERVAL_MS,
  ENERGY_AD_REWARD,
} from '../config';

/**
 * Callback when energy changes.
 */
export type EnergyChangeCallback = (energy: number, max: number) => void;

/**
 * EnergySystem manages the energy/stamina system.
 * Energy refills automatically over time and is consumed on merges.
 */
export class EnergySystem {
  private energy: number;
  private maxEnergy: number;
  private lastRefillTime: number;
  private onChange?: EnergyChangeCallback;

  constructor() {
    this.energy = MAX_ENERGY;
    this.maxEnergy = MAX_ENERGY;
    this.lastRefillTime = Date.now();
  }

  /**
   * Check if enough energy can be spent.
   */
  canSpend(amount: number = ENERGY_PER_MERGE): boolean {
    return this.energy >= amount;
  }

  /**
   * Spend energy. Returns true if successful, false if insufficient.
   */
  spend(amount: number = ENERGY_PER_MERGE): boolean {
    if (!this.canSpend(amount)) {
      return false;
    }

    this.energy -= amount;
    this.notifyChange();
    return true;
  }

  /**
   * Refill by a specific amount (capped at max).
   */
  refill(amount: number): void {
    this.energy = Math.min(this.energy + amount, this.maxEnergy);
    this.notifyChange();
  }

  /**
   * Refill to full.
   */
  refillFull(): void {
    this.energy = this.maxEnergy;
    this.notifyChange();
  }

  /**
   * Update energy based on elapsed time.
   * Called each frame; automatically refills 1 energy per ENERGY_REFILL_INTERVAL_MS.
   */
  update(now: number): void {
    if (this.energy >= this.maxEnergy) {
      return; // Already full
    }

    const elapsed = now - this.lastRefillTime;
    const refillCount = Math.floor(elapsed / ENERGY_REFILL_INTERVAL_MS);

    if (refillCount > 0) {
      this.refill(refillCount);
      this.lastRefillTime = now - (elapsed % ENERGY_REFILL_INTERVAL_MS);
    }
  }

  /**
   * Get current energy.
   */
  getEnergy(): number {
    return this.energy;
  }

  /**
   * Get max energy.
   */
  getMaxEnergy(): number {
    return this.maxEnergy;
  }

  /**
   * Get milliseconds until the next automatic refill.
   */
  getTimeTillNextRefill(now: number): number {
    if (this.energy >= this.maxEnergy) {
      return 0; // Already full
    }

    const elapsed = now - this.lastRefillTime;
    const timeUntilRefill = ENERGY_REFILL_INTERVAL_MS - (elapsed % ENERGY_REFILL_INTERVAL_MS);
    return Math.max(0, timeUntilRefill);
  }

  /**
   * Watch for energy changes.
   */
  setOnChange(callback: EnergyChangeCallback): void {
    this.onChange = callback;
  }

  /**
   * Give the player an energy reward from watching an ad.
   */
  watchAd(): void {
    this.refill(ENERGY_AD_REWARD);
  }

  /**
   * Notify listeners of energy change.
   */
  private notifyChange(): void {
    if (this.onChange) {
      this.onChange(this.energy, this.maxEnergy);
    }
  }

  /**
   * Set energy to a specific value (for testing/debugging).
   */
  setEnergy(amount: number): void {
    this.energy = Math.min(Math.max(0, amount), this.maxEnergy);
    this.notifyChange();
  }

  /**
   * Set max energy (for power-ups, perks, etc).
   */
  setMaxEnergy(amount: number): void {
    this.maxEnergy = amount;
    this.energy = Math.min(this.energy, this.maxEnergy);
    this.notifyChange();
  }
}
